/**
 * Despia push notification helpers.
 *
 * Real Despia SDK push commands:
 *   despia("registerpush://")              → request permission + register
 *   despia("getonesignalplayerid://", ["onesignalplayerid"]) → get player ID
 *   despia("setonesignalplayerid://?user_id=...") → set external user ID
 *   despia("settingsapp://")               → open iOS Settings
 *   sendlocalpushmsg://push.send?s=${seconds}=msg!${message}&!#${title}&!#
 */

import { isNativeApp, safeDespia, safeDespiaWithResponse } from "@/lib/safeDespia";

// ── constants ──────────────────────────────────────────────────────────
const MIN_DELAY_SECONDS = 5;
const REMINDER_STORAGE_PREFIX = "spotrai_native_sched_";

// ── session-level dedup for local push ─────────────────────────────────
// Tracks "title|hour:minute" combos already sent this JS session to prevent
// Despia from queuing the same one-shot notification multiple times.
const _scheduledThisSession = new Set<string>();

export function resetScheduleDedup() {
  _scheduledThisSession.clear();
}

type NativePushPermissionState = "prompt" | "granted" | "denied";

// ── helpers ────────────────────────────────────────────────────────────

/** Sanitise text for the Despia URL format (no &!# or newlines). */
const sanitise = (text: string) =>
  text.replace(/&!#/g, " ").replace(/[\r\n]+/g, " ").trim();

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const safeGet = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
};

function getNextOccurrence(hour: number, minute: number): Date {
  const now = new Date();
  const target = new Date(now);
  target.setHours(hour, minute, 0, 0);
  if (target.getTime() <= now.getTime()) {
    target.setDate(target.getDate() + 1);
  }
  return target;
}

/** Seconds from now until the next daily occurrence of hour:minute. */
export function secondsUntilNext(hour: number, minute: number): number {
  const now = new Date();
  const target = getNextOccurrence(hour, minute);
  return Math.max(MIN_DELAY_SECONDS, Math.floor((target.getTime() - now.getTime()) / 1000));
}

// ── public API ─────────────────────────────────────────────────────────

/**
 * Fetch the OneSignal Player ID using the real Despia SDK command.
 * Returns null if not available (device not registered).
 */
export async function fetchOneSignalPlayerId(): Promise<string | null> {
  if (!isNativeApp()) return null;
  try {
    const data = await safeDespiaWithResponse<Record<string, unknown>>(
      "getonesignalplayerid://",
      ["onesignalplayerid"],
    );
    console.log("[push] getonesignalplayerid:// response:", JSON.stringify(data));

    if (!data) return null;

    const pid = data.onesignalplayerid;
    if (typeof pid === "string" && pid.trim() && pid.trim().toLowerCase() !== "n/a") {
      return pid.trim();
    }
    return null;
  } catch (e) {
    console.warn("[push] fetchOneSignalPlayerId error:", e);
    return null;
  }
}

/**
 * Synchronous read — kept for fast non-async checks but may be stale.
 * Prefer fetchOneSignalPlayerId() for reliable reads.
 */
export function getOneSignalPlayerId(): string | null {
  if (!isNativeApp()) return null;
  try {
    // Dynamic import already loaded; try property read
    const despia = (globalThis as any).__despia_module?.default;
    if (!despia) return null;
    const pid = despia.onesignalplayerid;
    if (typeof pid === "string" && pid.trim() && pid.trim().toLowerCase() !== "n/a") {
      return pid.trim();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Wait for a valid OneSignal player ID with polling.
 */
export async function waitForOneSignalPlayerId(
  timeoutMs = 7000,
  intervalMs = 400,
): Promise<string | null> {
  if (!isNativeApp()) return null;

  // Try async fetch first
  let playerId = await fetchOneSignalPlayerId();
  if (playerId) return playerId;

  const start = Date.now();
  while (!playerId && Date.now() - start < timeoutMs) {
    await sleep(intervalMs);
    playerId = await fetchOneSignalPlayerId();
  }
  return playerId;
}

/**
 * Connect the current logged-in user ID to OneSignal (external user mapping).
 */
export async function setOneSignalExternalUserId(userId: string): Promise<boolean> {
  if (!isNativeApp() || !userId) return false;
  console.log("[push] Setting OneSignal external user ID:", userId);
  return safeDespia(`setonesignalplayerid://?user_id=${encodeURIComponent(userId)}`);
}

/**
 * Determine if iOS push notifications are authorized.
 *
 * Strategy: use `getonesignalplayerid://` — if it returns a valid player ID,
 * the device is registered with OneSignal, which means iOS permissions are granted.
 * If no player ID is returned, the user either hasn't been prompted or has denied.
 */
export async function getNativePushPermissionState(): Promise<NativePushPermissionState> {
  if (!isNativeApp()) return "prompt";

  const playerId = await fetchOneSignalPlayerId();
  console.log("[push] Permission check — playerId:", playerId);

  if (playerId) return "granted";
  return "prompt";
}

/**
 * Boolean helper used by the app UI.
 */
export async function checkNativePushPermissions(): Promise<boolean> {
  const state = await getNativePushPermissionState();
  return state === "granted";
}

/**
 * Request iOS notification permission via Despia.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNativeApp()) return false;
  console.log("[push] Requesting notification permission via registerpush://");
  return safeDespia("registerpush://");
}

/**
 * Number of days ahead to queue when scheduling a "daily" reminder.
 * Despia local pushes are one-shot, so we queue N future occurrences to
 * keep reminders firing even if the user doesn't reopen the app for weeks.
 * Every foreground pass re-tops-up any missing future days.
 */
const DAILY_QUEUE_DAYS = 21;

/**
 * Schedule daily local push notifications by queuing the next N occurrences.
 * Each occurrence is independently deduped so we never double-queue the same day.
 */
export async function scheduleDailyPush(
  hour: number,
  minute: number,
  identifier: string,
  title: string,
  body: string,
): Promise<boolean> {
  if (!isNativeApp()) return false;

  const storageKey = `${REMINDER_STORAGE_PREFIX}${identifier}`;
  const persistedRaw = safeGet(storageKey);
  // Persisted format: "YYYY-MM-DD|HH:MM,YYYY-MM-DD|HH:MM,..." (list of already-queued days)
  const persistedDays = new Set(
    (persistedRaw ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );

  const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const now = new Date();
  let anyQueued = false;

  for (let i = 0; i < DAILY_QUEUE_DAYS; i++) {
    const target = new Date(now);
    target.setHours(hour, minute, 0, 0);
    target.setDate(target.getDate() + i);
    if (target.getTime() <= now.getTime() + MIN_DELAY_SECONDS * 1000) continue;

    const targetDate = target.toISOString().slice(0, 10);
    const dayKey = `${targetDate}|${timeStr}`;
    const dedupKey = `${identifier}|${dayKey}`;

    if (_scheduledThisSession.has(dedupKey) || persistedDays.has(dayKey)) continue;

    const seconds = Math.max(
      MIN_DELAY_SECONDS,
      Math.floor((target.getTime() - now.getTime()) / 1000),
    );
    const cmd =
      `sendlocalpushmsg://push.send?s=${seconds}=msg!${sanitise(body)}&!#${sanitise(title)}&!#`;

    const ok = await safeDespia(cmd);
    if (ok) {
      _scheduledThisSession.add(dedupKey);
      persistedDays.add(dayKey);
      anyQueued = true;
      console.log("[push] Queued", identifier, "for", dayKey, "in", seconds, "s");
    }
  }

  // Prune past days to keep the string small, and persist.
  const today = new Date().toISOString().slice(0, 10);
  const pruned = Array.from(persistedDays).filter((k) => k.slice(0, 10) >= today);
  safeSet(storageKey, pruned.join(","));

  return anyQueued || pruned.length > 0;
}

/** Clear the persisted queue for an identifier (used when the user disables or changes time). */
export function clearScheduledQueue(identifier: string) {
  try { localStorage.removeItem(`${REMINDER_STORAGE_PREFIX}${identifier}`); } catch { /* no-op */ }
  for (const key of Array.from(_scheduledThisSession)) {
    if (key.startsWith(`${identifier}|`)) _scheduledThisSession.delete(key);
  }
}

/**
 * Open the iOS Settings page for this app so user can enable notifications.
 */
export async function openAppSettings(): Promise<boolean> {
  if (!isNativeApp()) return false;
  const opened = await safeDespia("settingsapp://");
  if (opened) return true;
  return safeDespia("opensettings://");
}
